import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Single mascot layer for /landing-v2.
 *
 * Three figures (yellow / purple / blue) live inside one fixed-position
 * layer. They are always opacity 1 — never fade in/out. Each scene is
 * defined by where the three figures sit relative to the centre of the
 * stop's box; figures that aren't in a given scene slide off-screen for
 * that scene and slide back in when needed. There's never a "ghost"
 * crossfade — the only motion is honest translate3d.
 *
 * Per-figure accessories (coffee mug, raised arms, board game, waving
 * arms, speech bubbles) are rendered INSIDE each figure's SVG using the
 * same figure-local coords as `MascotScene` etc. on `/`, so accessory
 * positions match the static design pixel-for-pixel. Accessories swap
 * at the leg's midpoint (where their wrapper opacity is 0 anyway) and
 * fade in via parkedness factor as the user settles into the scene.
 *
 * Plateau model 35/30/35 — 30% of every leg in the middle is the actual
 * run animation; the rest is parked dead zone.
 */

type Stop = "hero" | "scene-1" | "scene-2" | "scene-3" | "scene-4" | "cta";
const STOPS: Stop[] = ["hero", "scene-1", "scene-2", "scene-3", "scene-4", "cta"];

type FigureColor = "yellow" | "purple" | "blue";

const FILL: Record<FigureColor, string> = {
  yellow: "#F59E0B",
  purple: "#7C3AED",
  blue: "#2563EB",
};
const OUTLINE = "#2B2F54";

/** Each figure's position within a scene, relative to the stop's centre.
 *  OFFSCREEN pushes a figure clean past the viewport edge (≥ vw/2 + body_half
 *  + accessory radius, ≈ 280 px) so no corner pokes back into view. */
const OFFSCREEN = 280;

/** Per-figure vertical offset that applies ONLY during transit. While
 *  parked at any stop the trio sits on the same baseline (matches the
 *  static MascotScene layout). While running between stops, each figure
 *  picks up its own height — yellow stays level, purple jumps high,
 *  blue lopes low — so the journey feels chaotic and alive, but each
 *  arrival is clean. */
const FIGURE_TRANSIT_Y: Record<FigureColor, number> = {
  yellow: 0,
  purple: -50,
  blue: 30,
};
type Pos = { x: number; y: number };

const SCENES: Record<Stop, { yellow: Pos; purple: Pos; blue: Pos }> = {
  hero: {
    yellow:  { x: -80, y: 0 },
    purple:  { x: 0,   y: 0 },
    blue:    { x: 80,  y: 0 },
  },
  "scene-1": {
    yellow:  { x: 0,           y: 0 },
    purple:  { x: -OFFSCREEN,  y: 0 },
    blue:    { x: OFFSCREEN,   y: 0 },
  },
  "scene-2": {
    yellow:  { x: -70,         y: 0 },
    purple:  { x: -OFFSCREEN,  y: 0 },
    blue:    { x: 70,          y: 0 },
  },
  "scene-3": {
    yellow:  { x: 70,          y: 0 },
    purple:  { x: -70,         y: 0 },
    blue:    { x: OFFSCREEN,   y: 0 },
  },
  "scene-4": {
    yellow:  { x: -OFFSCREEN,  y: 0 },
    purple:  { x: OFFSCREEN,   y: 0 },
    blue:    { x: 0,           y: 0 },
  },
  cta: {
    yellow:  { x: -80, y: 0 },
    purple:  { x: 0,   y: 0 },
    blue:    { x: 80,  y: 0 },
  },
};

// Continuous motion model: figures sit still in a small parking zone around
// each stop (PARKING_HALF px above and below the stop's scrollY), and
// smoothly interpolate between two adjacent stops everywhere else. There
// are no "leg boundaries" — motion is one continuous function of scrollY
// across the whole page.
const PARKING_HALF = 80;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function smoothstep(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Accessory SVG groups — drawn in figure-local coords (same as MascotScene).
// Each function returns an <g> placed inside the figure's SVG; positioning
// inside the figure body is identical to the static design on `/`.
// ---------------------------------------------------------------------------

function CoffeeMugAcc() {
  return (
    <g transform="translate(20 60)">
      <rect x="-10" y="-2" width="20" height="22" rx="3" fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2.5" />
      <path d="M 10 4 q 6 0 6 6 q 0 6 -6 6" fill="none" stroke={OUTLINE} strokeWidth="2.5" />
      <path d="M -4 -6 q -2 -4 0 -8" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
      <path d="M 2 -6 q 2 -4 0 -8" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}

function RaisedArmsAcc({ color }: { color: FigureColor }) {
  return (
    <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
      <path d="M -22 58 q -10 -15 -6 -32" />
      <path d="M 22 58 q 10 -15 6 -32" />
      <circle cx="-28" cy="25" r="4" fill={FILL[color]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="28" cy="25" r="4" fill={FILL[color]} stroke={OUTLINE} strokeWidth="2" />
    </g>
  );
}

function BoardGameAcc() {
  return (
    <g transform="translate(-20 65) rotate(-8)">
      <rect x="-14" y="0" width="28" height="20" rx="3" fill="#FFFFFF" stroke={OUTLINE} strokeWidth="2.5" />
      <circle cx="-7" cy="7" r="1.8" fill={OUTLINE} />
      <circle cx="0" cy="10" r="1.8" fill={OUTLINE} />
      <circle cx="7" cy="13" r="1.8" fill={OUTLINE} />
    </g>
  );
}

/** Waving arm. `side="right"` puts the raised/waving arm on the figure's
 *  right. Both arms get hand circles and a slight curve — the waving one
 *  curves up and animates, the resting one curves down so it reads as a
 *  hand hanging loose at the side. */
function WaveArmAcc({ color, side }: { color: FigureColor; side: "right" | "left" }) {
  const sign = side === "right" ? 1 : -1;
  return (
    <g stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" fill="none">
      {/* Resting arm — attaches to shoulder like the waving one, curves
         out then down. Hand sits well clear of the body so it doesn't
         look glued to the side. */}
      <path d={`M ${sign * -22} 58 q ${sign * -14} 12 ${sign * -12} 28`} />
      <circle
        cx={sign * -34}
        cy={86}
        r="4"
        fill={FILL[color]}
        stroke={OUTLINE}
        strokeWidth="2"
      />
      {/* Waving arm — raised, with hand circle, animated. */}
      <g className="nf-wave-arm" style={{ transformOrigin: `${sign * 22}px 58px` }}>
        <path d={`M ${sign * 22} 58 q ${sign * 10} -16 ${sign * 10} -30`} />
        <circle
          cx={sign * 32}
          cy={26}
          r="4"
          fill={FILL[color]}
          stroke={OUTLINE}
          strokeWidth="2"
        />
      </g>
    </g>
  );
}

/** Speech bubble drawn beside the figure's head. Dots pulse in sequence,
 *  driven by explicit `.nf-dot-N` classes (more reliable than nth-of-type
 *  which selects within ALL siblings — there are multiple circles inside
 *  a figure). */
function SpeechBubbleAcc({ side }: { side: "right" | "left" }) {
  const flip = side === "left" ? -1 : 1;
  return (
    <g className="nf-bubble">
      <path
        d={flip === 1
          ? "M 14 -2 q 0 -12 14 -12 h 28 q 14 0 14 12 v 10 q 0 12 -14 12 h -18 l -8 8 v -8 h -2 q -14 0 -14 -12 z"
          : "M -14 -2 q 0 -12 -14 -12 h -28 q -14 0 -14 12 v 10 q 0 12 14 12 h 18 l 8 8 v -8 h 2 q 14 0 14 -12 z"}
        fill="#FFFFFF"
        stroke={OUTLINE}
        strokeWidth="2"
      />
      <circle className="nf-dot nf-dot-1" cx={flip * 34} cy={2} r={2.2} fill={OUTLINE} />
      <circle className="nf-dot nf-dot-2" cx={flip * 42} cy={2} r={2.2} fill={OUTLINE} />
      <circle className="nf-dot nf-dot-3" cx={flip * 50} cy={2} r={2.2} fill={OUTLINE} />
    </g>
  );
}

/** Per-scene per-color accessory; null means no accessory in this scene. */
function accessoryFor(color: FigureColor, scene: Stop): ReactNode {
  switch (scene) {
    case "hero":
    case "cta":
      if (color === "yellow") return <CoffeeMugAcc />;
      if (color === "purple") return <RaisedArmsAcc color="purple" />;
      if (color === "blue") return <BoardGameAcc />;
      return null;
    case "scene-1":
      return null;
    case "scene-2":
      if (color === "yellow") return <WaveArmAcc color="yellow" side="right" />;
      if (color === "blue") return <WaveArmAcc color="blue" side="left" />;
      return null;
    case "scene-3":
      if (color === "yellow") return <SpeechBubbleAcc side="right" />;
      if (color === "purple") return <SpeechBubbleAcc side="left" />;
      return null;
    case "scene-4":
      if (color === "blue") return <WaveArmAcc color="blue" side="right" />;
      return null;
  }
}

// ---------------------------------------------------------------------------
// Figure component — same SVG layout the static MascotScene uses, plus a
// slot for an accessory rendered in the same figure-local coords.
// ---------------------------------------------------------------------------

type FigureHandle = {
  root: HTMLDivElement | null;
  body: SVGGElement | null;
  shadow: SVGEllipseElement | null;
  acc: SVGGElement | null;
};

const Figure = forwardRef<FigureHandle, { color: FigureColor; accessory: ReactNode }>(
  function Figure({ color, accessory }, ref) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<SVGGElement | null>(null);
    const shadowRef = useRef<SVGEllipseElement | null>(null);
    const accRef = useRef<SVGGElement | null>(null);

    useRefImperative(ref, () => ({
      root: rootRef.current,
      body: bodyRef.current,
      shadow: shadowRef.current,
      acc: accRef.current,
    }));

    const fill = FILL[color];
    // ViewBox is generous — gives accessories (waving arm, raised arms,
    // speech bubble dots) clear room without clipping. svg scale stays
    // the same (~1.143 px per viewBox unit) so the figure body renders
    // at the same on-screen size as before.
    return (
      <div
        ref={rootRef}
        className="absolute will-change-transform"
        style={{
          left: "50%",
          top: "50%",
          width: 184,
          height: 160,
          marginLeft: -92,
          marginTop: -85,
        }}
      >
        {/* Wider viewBox so speech bubble's right edge (x=70) and waving
           arm extents (x=±32) sit comfortably inside the rendered SVG
           rectangle — no reliance on overflow:visible to keep them on
           screen, which some mobile browsers ignore. */}
        <svg
          viewBox="-80 -25 160 140"
          width={184}
          height={160}
          style={{ overflow: "visible" }}
        >
          {/* Ground shadow — separate layer so it stays on the floor while
             the body bobs above. RAF scales/dims it as the body jumps. */}
          <ellipse
            ref={shadowRef}
            cx="0"
            cy="108"
            rx="44"
            ry="5"
            fill="#000"
            opacity="0.13"
            style={{ transformBox: "view-box", transformOrigin: "0px 108px" }}
          />
          {/* Body group — the whole mascot above the ground. RAF translates
             this group up (negative y) during transit for the running bob;
             the shadow stays put. */}
          <g
            ref={bodyRef}
            style={{ transformBox: "view-box", transformOrigin: "0px 92px" }}
          >
            <circle cx="0" cy="22" r="24" fill={OUTLINE} />
            <path d="M -32 102 a 32 50 0 0 1 64 0 z" fill={OUTLINE} transform="translate(0 -8)" />
            <circle cx="0" cy="22" r="20" fill={fill} />
            <path d="M -28 92 a 28 42 0 0 1 56 0 z" fill={fill} transform="translate(0 -4)" />
            <g ref={accRef} style={{ opacity: 0, transition: "opacity 250ms ease" }}>
              {accessory}
            </g>
          </g>
        </svg>
      </div>
    );
  }
);

// Tiny helper that lets us attach an imperative handle without pulling in
// useImperativeHandle for one line.
function useRefImperative<T>(
  ref: React.Ref<T> | null,
  factory: () => T,
) {
  useEffect(() => {
    if (!ref) return;
    const value = factory();
    if (typeof ref === "function") ref(value);
    else (ref as React.MutableRefObject<T | null>).current = value;
    return () => {
      if (typeof ref === "function") ref(null as unknown as T);
      else (ref as React.MutableRefObject<T | null>).current = null;
    };
    // factory intentionally re-runs each render — refs may have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function JourneyMascots() {
  const yellowH = useRef<FigureHandle | null>(null);
  const purpleH = useRef<FigureHandle | null>(null);
  const blueH   = useRef<FigureHandle | null>(null);

  // Whichever stop is the closer one of the current leg — that drives the
  // accessory currently rendered. We swap at midpoint where parkedness=0.
  const [currentStop, setCurrentStop] = useState<Stop>("hero");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document.querySelectorAll<HTMLElement>("[data-mascot-stop]").forEach((el) => {
        el.setAttribute("data-active", "");
      });
      [yellowH.current, purpleH.current, blueH.current].forEach((h) => {
        if (h?.acc) h.acc.style.opacity = "1";
      });
      return;
    }

    let frameId = 0;
    const startTime = performance.now();
    let currentActive: HTMLElement | null = null;
    let lastClosestStop: Stop | null = null;

    let scrollAt: Record<Stop, number> = {} as Record<Stop, number>;
    let vw = window.innerWidth;
    let vh = window.innerHeight;

    function refresh() {
      const newVw = window.innerWidth;
      // Only honour vh changes that come with a vw change (real resize /
      // rotation). Mobile Safari fires resize when its URL bar shows/hides,
      // changing only vh by ~80 px — if we recomputed scrollAt then, every
      // figure would jump as the bar toggled. Holding vh stable across
      // those phantom resizes keeps the layer perfectly still.
      if (newVw !== vw) {
        vw = newVw;
        vh = window.innerHeight;
      }
      STOPS.forEach((id, idx) => {
        const el = document.querySelector<HTMLElement>(`[data-mascot-stop="${id}"]`);
        if (!el) return;
        // Walk the offsetTop chain to get the element's LAYOUT page-Y,
        // which is stable regardless of whether the element is sticky-
        // pinned at refresh time. getBoundingClientRect.top reflects
        // current rendered position which during sticky pinning gives
        // a pageY that drifts with scrollY — that's what made parking
        // direction-dependent in earlier passes.
        let layoutY = 0;
        let cur: HTMLElement | null = el;
        while (cur) {
          layoutY += cur.offsetTop;
          cur = cur.offsetParent as HTMLElement | null;
        }
        let sa = layoutY + el.offsetHeight / 2 - vh / 2;
        if (idx === 0) sa = Math.max(0, sa);
        scrollAt[id] = sa;
      });
    }
    refresh();
    window.addEventListener("resize", refresh, { passive: true });
    const settleTimer = window.setTimeout(refresh, 800);

    function setActive(stop: Stop | null) {
      const next = stop
        ? document.querySelector<HTMLElement>(`[data-mascot-stop="${stop}"]`)
        : null;
      if (next === currentActive) return;
      currentActive?.removeAttribute("data-active");
      next?.setAttribute("data-active", "");
      currentActive = next;
    }

    function stopCentre(stop: Stop): { x: number; y: number } {
      const el = document.querySelector<HTMLElement>(`[data-mascot-stop="${stop}"]`);
      if (!el) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      // Use LIVE viewport size here, not cached. The figure DIV is anchored
      // via top: 50%, which CSS resolves against the live visual viewport;
      // using cached vh would produce offsets that don't match where the
      // browser renders the sticky placeholder when the mobile URL bar
      // shows/hides and reshapes the visual viewport.
      const lvw = window.innerWidth;
      const lvh = window.innerHeight;
      return {
        x: r.left + r.width / 2 - lvw / 2,
        y: r.top + r.height / 2 - lvh / 2,
      };
    }

    function tick(now: number) {
      const t = (now - startTime) / 1000;
      const scrollY = window.scrollY;

      // Build a sorted list of available stops (some might be missing on cold
      // start if their DOM element wasn't found by refresh()).
      const stopList = STOPS.filter((s) => scrollAt[s] !== undefined);
      if (stopList.length === 0) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      // Parkedness — how "settled" the user is at each stop.
      //   - First stop (Hero): scrollY-based. Parked while scrollY ≤ scrollAt.
      //     Hero is never sticky-pinned (top of page) so live measurement
      //     would never reach 1; the scrollY clamp handles it.
      //   - Last stop (CTA): symmetric — parked while scrollY ≥ scrollAt.
      //   - Middle stops (sticky): use LIVE measurement — when the sticky
      //     placeholder is pinned, its rect centre sits at vh/2 exactly,
      //     so dist = 0 and parkedness = 1 across the whole sticky range
      //     of the section, not just at one scrollY point.
      const lastIdx = stopList.length - 1;
      // Live max scroll, recomputed each frame. The CTA sits at the page bottom,
      // and mobile Safari changes the viewport height when its URL bar shows/
      // hides AFTER load — a cached target would land past the reachable bottom,
      // so the trio never parks until you over-scroll. Clamping the last stop's
      // target to the live max lands it exactly at the bottom you can reach.
      const liveMax = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const lastStop = stopList[lastIdx];
      const lastTarget = Math.min(scrollAt[lastStop], liveMax);
      const PARK_CORE = 30; // px window where parkedness stays at 1
      function parkednessFor(stop: Stop, idx: number): number {
        if (idx === 0) {
          const dist = scrollY - scrollAt[stop];
          if (dist < 0) return 1;
          return Math.max(0, 1 - dist / PARKING_HALF);
        }
        if (idx === lastIdx) {
          const dist = scrollY - lastTarget;
          if (dist > 0) return 1;
          return Math.max(0, 1 - Math.abs(dist) / PARKING_HALF);
        }
        const el = document.querySelector<HTMLElement>(`[data-mascot-stop="${stop}"]`);
        if (!el) return 0;
        const r = el.getBoundingClientRect();
        const centreInView = r.top + r.height / 2;
        // Live viewport — when the mobile URL bar toggles, the sticky
        // element repositions with the live viewport, and parkedness must
        // be measured against that same live centre, not the cached one,
        // or the trio drifts out of the placeholder until the next bar
        // toggle equalises them.
        const dist = Math.abs(centreInView - window.innerHeight / 2);
        if (dist <= PARK_CORE) return 1;
        return Math.max(0, 1 - (dist - PARK_CORE) / PARKING_HALF);
      }

      // The accessories follow whichever stop has the highest parkedness.
      // While in transit, all stops have parkedness 0 → no accessories show.
      let bestStop: Stop = stopList[0];
      let bestParked = 0;
      for (let i = 0; i < stopList.length; i++) {
        const p = parkednessFor(stopList[i], i);
        if (p > bestParked) { bestParked = p; bestStop = stopList[i]; }
      }
      setActive(bestParked > 0 ? bestStop : null);
      if (bestStop !== lastClosestStop && bestParked > 0) {
        lastClosestStop = bestStop;
        setCurrentStop(bestStop);
      }

      // Position the figures by walking the global timeline:
      //   scrollY <= stopList[0] − HALF                  → parked at first
      //   |scrollY − scrollAt[i]| <= HALF                → parked at i
      //   transit gap between two stops                  → smooth lerp
      //   scrollY >= last + HALF                         → parked at last
      function figurePos(color: FigureColor): { x: number; y: number } {
        // Clamp to first stop's pose if we're before it.
        if (scrollY <= scrollAt[stopList[0]] - PARKING_HALF) {
          return centreFor(stopList[0], color);
        }
        // Clamp to last stop's pose if we're after it (live-clamped target).
        if (scrollY >= lastTarget + PARKING_HALF) {
          return centreFor(lastStop, color);
        }
        // Parked at any stop?
        for (const stop of stopList) {
          const target = stop === lastStop ? lastTarget : scrollAt[stop];
          if (Math.abs(scrollY - target) <= PARKING_HALF) {
            return centreFor(stop, color);
          }
        }
        // Transit between two adjacent stops — smooth lerp across the gap.
        for (let i = 0; i < stopList.length - 1; i++) {
          const a = stopList[i];
          const b = stopList[i + 1];
          const transitStart = scrollAt[a] + PARKING_HALF;
          const transitEnd = (b === lastStop ? lastTarget : scrollAt[b]) - PARKING_HALF;
          if (scrollY > transitStart && scrollY < transitEnd) {
            const span = Math.max(1, transitEnd - transitStart);
            const tRaw = (scrollY - transitStart) / span;
            const ttSmooth = smoothstep(tRaw);
            const pa = centreFor(a, color);
            const pb = centreFor(b, color);
            return {
              x: lerp(pa.x, pb.x, ttSmooth),
              y: lerp(pa.y, pb.y, ttSmooth),
            };
          }
        }
        return centreFor(stopList[0], color);
      }

      function centreFor(stop: Stop, color: FigureColor): { x: number; y: number } {
        const c = stopCentre(stop);
        const off = SCENES[stop][color];
        // Universal upward bias — body sits 18 px above each placeholder's
        // geometric centre, so the long ground-shadow tail doesn't crowd
        // the button/text immediately below the placeholder. Especially
        // important for the CTA, where the placeholder is right above
        // the "Войти через Telegram" button.
        return { x: c.x + off.x, y: c.y + off.y - 18 };
      }

      // When no stop has high parkedness we're mid-transit — figure body
      // bobs up and down as it runs, but the shadow stays on the ground.
      const inTransit = bestParked < 0.6;
      const accOpacity = Math.min(1, bestParked * 2);
      // 0 at full parking → 1 in full transit. Per-figure vertical offset
      // ramps in with this factor, so trio is level at parks and chaotic in
      // motion.
      const transitFactor = 1 - bestParked;

      function place(h: FigureHandle | null, color: FigureColor, idx: number) {
        if (!h?.root) return;
        const pos = figurePos(color);
        const figureY = FIGURE_TRANSIT_Y[color] * transitFactor;

        // The DIV positions the figure; no bob here so the shadow (rendered
        // inside the same DIV but as a separate group below) stays on the
        // ground and only the body group lifts.
        h.root.style.transform = `translate3d(${pos.x}px, ${pos.y + figureY}px, 0)`;

        // Body bob + tilt — translate the inner <g ref={bodyRef}> upward.
        // Bob amplitude is bigger (12 px) for clearer running motion.
        const bobAmp = inTransit ? 12 : 0;
        const bob = -Math.abs(Math.sin(t * 6 + idx * 1.1)) * bobAmp;
        const tilt = inTransit ? Math.sin(t * 6 + idx * 1.1) * 3 : 0;
        if (h.body) {
          h.body.style.transform = `translate(0px, ${bob}px) rotate(${tilt}deg)`;
        }

        // Shadow physics — shrinks and dims as the body rises above it,
        // recovers as it lands. Scale 1.0 on ground → 0.7 at peak jump.
        if (h.shadow) {
          const bobMag = Math.abs(bob) / 12; // 0..1
          const shadowScale = 1 - bobMag * 0.3;
          h.shadow.style.transform = `scale(${shadowScale}, ${shadowScale})`;
          h.shadow.style.opacity = String(0.13 - bobMag * 0.05);
        }

        if (h.acc) h.acc.style.opacity = String(accOpacity);
      }
      place(yellowH.current, "yellow", 0);
      place(purpleH.current, "purple", 1);
      place(blueH.current,   "blue",   2);

      frameId = requestAnimationFrame(tick);
    }
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", refresh);
      window.clearTimeout(settleTimer);
      currentActive?.removeAttribute("data-active");
    };
  }, []);

  return (
    <div
      aria-hidden
      className="fixed inset-0 pointer-events-none overflow-hidden"
      // z-index 0 keeps figures behind Landing content (text, buttons, the
      // map widget) so they never cover information. To stop them being
      // dimmed by the hero's radial gradient, the gradient is dropped in v2
      // mode (see Landing.tsx — `hideInlineMascots` branch).
      style={{ zIndex: 0 }}
    >
      <Figure ref={yellowH} color="yellow" accessory={accessoryFor("yellow", currentStop)} />
      <Figure ref={purpleH} color="purple" accessory={accessoryFor("purple", currentStop)} />
      <Figure ref={blueH}   color="blue"   accessory={accessoryFor("blue",   currentStop)} />
      <style>{`
        @keyframes nfWaveArm {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-15deg); }
          75% { transform: rotate(15deg); }
        }
        .nf-wave-arm { animation: nfWaveArm 1s ease-in-out infinite; }
        @keyframes nfBubblePulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; }
        }
        .nf-dot { opacity: 0.35; }
        .nf-dot-1 { animation: nfBubblePulse 1.4s ease-in-out 0s infinite; }
        .nf-dot-2 { animation: nfBubblePulse 1.4s ease-in-out 0.25s infinite; }
        .nf-dot-3 { animation: nfBubblePulse 1.4s ease-in-out 0.5s infinite; }
      `}</style>
    </div>
  );
}
